import { inject, Injectable } from '@angular/core';

import { Subscription, take } from 'rxjs';

import {
  StepDefinition,
  UserInvitationFlow,
  UserInvitationStep,
  UserInvitationStepI,
} from '@core/tellent-types';
import { DialogRef, DialogService, DialogWidth } from '@core/user-interface-legacy';

import { stepComponentMap } from './invitation-flow-component.map';
import { InvitationFlowStateService } from './invitation-flow-state.service';
import { UserInvitationFlowSelectionDialogComponent } from '../user-invitation-flow-selection-dialog/user-invitation-flow-selection-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class UserInvitationFlowFacade {
  private readonly dialogService = inject(DialogService);
  private readonly flowService = inject(InvitationFlowStateService);

  private readonly stepComponentMap: Record<UserInvitationStep, UserInvitationStepI> =
    stepComponentMap;

  private activeDialogRef?: DialogRef;
  private activeFlowSub?: Subscription;

  public startFlow() {
    this.cancelCurrentFlow();

    this.activeDialogRef = this.dialogService.openDialog(
      UserInvitationFlowSelectionDialogComponent,
      {},
      {
        width: DialogWidth.Medium,
      },
    );

    this.activeFlowSub = this.activeDialogRef.afterClosed
      .pipe(take(1))
      .subscribe((flowType: UserInvitationFlow | undefined) => {
        if (flowType) {
          // console.log('Flow:', flowType);
          const firstStep = this.flowService.initializeFlow(flowType);
          if (firstStep) {
            this.openStepDialog(firstStep);
          }
        }
      });
  }

  public cancelCurrentFlow() {
    if (this.activeDialogRef) {
      this.activeDialogRef.close();
    }
    if (this.activeFlowSub) {
      this.activeFlowSub.unsubscribe();
    }
  }

  private openStepDialog(stepDef: StepDefinition) {
    const stepToOpen = this.stepComponentMap[stepDef.step];
    const { dialogComponent, dialogConfig } = stepToOpen;

    if (!stepToOpen) {
      console.error(`No component registered for step: ${stepDef.step}`);
      this.cancelCurrentFlow();
      return;
    }

    this.activeFlowSub?.unsubscribe();

    this.flowService.invitationData$.pipe(take(1)).subscribe(data => {
      // console.log('Data:', data);
      this.activeDialogRef = this.dialogService.openDialog(
        dialogComponent,
        {
          ...data,
          ...stepDef.initialData,
        },
        dialogConfig,
      );
    });

    if (this.activeDialogRef) {
      this.activeFlowSub = this.activeDialogRef.afterClosed.pipe(take(1)).subscribe(result => {
        if (result === undefined) {
          // console.log('Flow cancelled by user.');
          return; // Stop the flow
        }

        const nextStepDef = this.flowService.advanceFlow(result);

        if (nextStepDef) {
          this.openStepDialog(nextStepDef); // Recursion
        } else {
          this.handleFlowCompletion();
        }
      });
    }
  }

  private handleFlowCompletion() {
    // console.log('Flow is complete! ');
    this.flowService.finalizeFlow().subscribe({
      // next: response => console.log('Invitation sent!', response),
      // error: err => console.error('Failed to send invitation:', err),
      complete: () => {
        this.activeDialogRef = undefined;
        this.activeFlowSub = undefined;
      },
    });
  }
}
